import { tagLabelKey, type Chip } from '../classify';
import { useT } from '../i18n/useT';

/**
 * Market-tag filter (#875 v2, docs/research/CLASSIFICATION.md "3b. App behaviour"): ONE row = "All", then the
 * selected chips (first, highlighted; tap removes), then the chips still offered for the narrowed list with their
 * counts (tap adds). Several chips combine per rules.ui.combine ('and' by default). "All" clears the selection.
 */
export interface TagChipsProps {
  /** chipRow().selected */
  selected: Chip[];
  /** chipRow().offered */
  offered: Chip[];
  onChange: (tags: string[]) => void;
}

const chip = 'shrink-0 rounded-full border px-2.5 py-1 text-xs transition-colors';
const chipOn = 'border-accent bg-accent/15 text-accent';
const chipOff = 'border-border text-muted hover:text-fg';

export default function TagChips({ selected, offered, onChange }: TagChipsProps) {
  const t = useT();
  if (selected.length === 0 && offered.length === 0) return null;
  const ids = selected.map(([id]) => id);
  const button = (id: string, n: number, on: boolean) => (
    <button
      key={id}
      type="button"
      aria-pressed={on}
      onClick={() => onChange(on ? ids.filter((x) => x !== id) : [...ids, id])}
      className={`${chip} ${on ? chipOn : chipOff}`}
      data-testid={`tag-chip-${id}`}
    >
      {t(tagLabelKey(id))} <span className="num opacity-70">{n}</span>
    </button>
  );
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={t('catalog.tag_filter')} data-testid="tag-chips">
      <button
        type="button"
        className={`${chip} ${ids.length === 0 ? chipOn : chipOff}`}
        aria-pressed={ids.length === 0}
        onClick={() => onChange([])}
        data-testid="tag-chip-all"
      >
        {t('catalog.tag_all')}
      </button>
      {selected.map(([id, n]) => button(id, n, true))}
      {offered.map(([id, n]) => button(id, n, false))}
    </div>
  );
}
